import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { VariableType } from '@selfai-platform/pipeline-common';
import { TreeNode } from 'primeng/api';
import { VariableRowState } from '../models';
import { TreeDataItem } from '../models/tree-data-item.model';
import { VariablesTreeDataService } from './variables-tree-data.service';
export interface VariableEditForm {
  key: FormControl<string | null>;
  value: FormControl<string | null>;
  description: FormControl<string | null>;
  regexRule: FormControl<string | null>;
  parentGroup: FormControl<TreeNode<TreeDataItem> | null>;
}

@Injectable()
export class VariablesFormService {
  private readonly treeDataService = inject(VariablesTreeDataService);
  private readonly editForm: FormGroup<VariableEditForm> = new FormGroup({
    key: new FormControl<string | null>(null, [Validators.required]),
    value: new FormControl<string | null>(null),
    description: new FormControl<string | null>(null),
    regexRule: new FormControl<string | null>(null),
    parentGroup: new FormControl<TreeNode<TreeDataItem> | null>(null),
  });

  getForm(): FormGroup<VariableEditForm> {
    return this.editForm;
  }

  patchFormValue(
    values: Partial<{
      key: string;
      value: string | null;
      description: string;
      regexRule: string | null;
      parentGroup: TreeNode<TreeDataItem> | null;
    }>,
  ): void {
    this.editForm.patchValue(values);
  }

  resetForm(): void {
    this.editForm.reset();
  }

  isFormValid(): boolean {
    return this.editForm.valid;
  }

  getFormValue() {
    return this.editForm.getRawValue();
  }

  setValidators(type: VariableType): void {
    if (type === VariableType.VARIABLE) {
      this.editForm.controls.key.setValidators([Validators.required]);
      this.editForm.controls.value.setValidators([Validators.required]);
    } else {
      this.editForm.controls.key.setValidators([Validators.required]);
      this.editForm.controls.value.clearValidators();
    }
    this.editForm.controls.key.updateValueAndValidity();
    this.editForm.controls.value.updateValueAndValidity();
  }

  buildVariableRequest(
    formValue: Partial<{
      key: string | null;
      value: string | null;
      description: string | null;
      regexRule: string | null;
      parentGroup: TreeNode<TreeDataItem> | null;
    }>,
    type: VariableType,
  ) {
    return {
      varType: type,
      value: {
        key: formValue.key!,
        value: type === VariableType.VARIABLE ? formValue.value! : undefined,
        description: formValue.description || undefined,
        regexRule: type === VariableType.VARIABLE ? formValue.regexRule || undefined : undefined,
      },
      parentId: formValue.parentGroup?.data?.id || undefined,
    };
  }

  getAbsolutePath(): string {
    const parentGroup = this.editForm.controls.parentGroup.value;
    const parentGroupId = parentGroup?.key || '';
    const itemName = this.editForm.controls.key.value || '';

    return this.treeDataService.buildAbsoluteVariablePath(parentGroupId, itemName);
  }

  setFieldsDisabledState(rowState: VariableRowState): void {
    const isEditingValueOnly = rowState === VariableRowState.EDITING_VALUE_ONLY;

    if (isEditingValueOnly) {
      this.editForm.controls.key.disable();
      this.editForm.controls.description.disable();
      this.editForm.controls.regexRule.disable();
      this.editForm.controls.parentGroup.disable();

      this.editForm.controls.value.enable();
    } else {
      this.editForm.controls.key.enable();
      this.editForm.controls.description.enable();
      this.editForm.controls.regexRule.enable();
      this.editForm.controls.parentGroup.enable();
      this.editForm.controls.value.enable();
    }
  }
}
